<template>
  <div class="tw-inline-block">
    <IconButton
      color="outline-secondary"
      @click="openModal('edit-written-content')"
    >
      <PenIcon />
    </IconButton>

    <FormModal header="Edit URLs" name="edit-written-content">
      <FormModalBody
        :is-button-loading="form.processing"
        footer-button-text="Save"
        @submit="saveForm"
      >
        <div>
          <div class="tw-flex tw-flex-col tw-space-y-4">
            <div
              v-for="(link, index) in form.links"
              :key="index"
              class="tw-inline-flex"
            >
              <FormInput
                v-model="link.url"
                class="tw-grow"
                :is-error="link.isDeleted || hasLinkErrors(index)"
                :disabled="link.isDeleted"
                :errors="getLinkErrors(index)"
                type="text"
                @input="handleLinkChange(link, index)"
                test_id="link-url-input"
              >
                <template #label>URL</template>
              </FormInput>
              <button
                @click="deleteLink(link, index)"
                class="tw--mr-2 tw-ml-2 tw-rounded-md tw-px-2 tw-text-neutral-400 tw-transition-colors hover:tw-text-neutral-500 focus:tw-outline-none focus:tw-ring focus:tw-ring-neutral-200"
                :class="[
                  hasLinkErrors(index) ? 'tw-mt-[10px]' : 'tw-mt-[28px]',
                ]"
              >
                <component
                  :is="getDeleteIcon(link.isDeleted)"
                  :test_id="`link-${link.id}-delete`"
                  class="tw-h-4 tw-w-4"
                />
              </button>
            </div>
          </div>
          <div class="tw-mt-4">
            <BaseButton @click="addLink" color="outline-secondary" size="small">
              <RoundedAddIcon class="tw-mr-2 tw-h-[14px] tw-w-[14px]" />
              Add another
            </BaseButton>
          </div>
        </div>
      </FormModalBody>
    </FormModal>
  </div>
</template>

<script setup>
import IconButton from '@/shared/components/IconButton.vue'
import FormModalBody from '@/shared/components/modals/FormModal/FormModalBody.vue'
import FormModal from '@/shared/components/modals/FormModal/FormModal.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import { useForm } from '@inertiajs/vue3'
import UndoIcon from '@/assets/images/icons/undo.svg'
import BinIcon from '@/assets/images/icons/bin.svg'
import RoundedAddIcon from '@/assets/images/icons/rounded-add.svg'
import PenIcon from '@/assets/images/icons/pen.svg'
import { watch } from 'vue'
import { useModals } from '@/shared/composables/useModals.js'

const props = defineProps({
  links: {
    type: Array,
    required: true,
  },
})

const { closeModal, openModal } = useModals()

watch(
  () => props.links,
  (links) => {
    form.links = links.map((link) => ({
      ...link,
      status: 'NONE',
      isDeleted: false,
    }))
  },
  { deep: true },
)

const form = useForm({
  links: props.links.map((link) => ({
    ...link,
    status: 'NONE',
    isDeleted: false,
  })),
})

const addLink = () => {
  form.links = form.links.concat([
    { link: null, status: 'CREATED', isDeleted: false },
  ])
}

const getDeleteIcon = (isDeleted) => {
  return isDeleted ? UndoIcon : BinIcon
}

const getLinkErrors = (index) => {
  const linkErrors = form.errors[index]
  if (!linkErrors) return []
  if (!Object.keys(linkErrors.errors).includes('url')) return []
  return linkErrors.errors.url
}

const hasLinkErrors = (index) => {
  return Boolean(getLinkErrors(index).length)
}

const handleLinkChange = (link, index) => {
  if (hasLinkErrors(index)) {
    form.errors[index].errors.url = []
  }

  if (link.status === 'CREATED') return
  form.links[index].status = 'EDITED'
}

const deleteLink = (link, index) => {
  if (link.status === 'CREATED') {
    form.links.splice(index, 1)
    return
  }

  form.links[index].isDeleted = !form.links[index].isDeleted
}

const saveForm = () => {
  form
    .transform((data) => ({
      links: data.links,
    }))
    .patch('/profile/mentor/links', {
      preserveScroll: true,
      onSuccess: () => {
        closeModal('edit-written-content')
        // router.reload({ only: ['reviewsOrdered'] })
      },
    })
}
</script>
